// LE-00 (promo): セグメント抽出の判定（純関数）。
// tenant-promo 管理画面（人数プレビュー）と functions（promoSendSegmentPush の宛先解決）の
// 双方から参照する単一の真実源。Web/Node SDK 依存は持たない。
import { userFirstSourceBucket } from './promoFirstSource.js';
import { interestStrength, stageRank } from './promoInterest.js';
import { isHotDeliverySuppressed, isInterestOptedOut } from './promoHot.js';
import { resolveEffectiveStage, resolveInterestEngine } from './promoEngine.js';
import { evaluateVisitChurn } from './promoChurn.js';
import { todayJst, addDaysJst } from './dateJst.js';
const DAY_MS = 24 * 60 * 60 * 1000;
/** M-166: クーポン保有条件が「絞り込みとして成立している」か（couponIds が 1 つ以上）。 */
export function hasCouponHoldingCondition(segment) {
    return (!!segment.couponHolding &&
        Array.isArray(segment.couponHolding.couponIds) &&
        segment.couponHolding.couponIds.length > 0);
}
/** minLevel の比較基準となる強度（estimated は最弱 score=low 基準）。 */
function levelBaselineStrength(level) {
    return interestStrength(level === 'estimated'
        ? { level, score: 'low', updatedAt: '' }
        : { level, updatedAt: '' });
}
/**
 * セグメント条件が 1 つも指定されていないか（誤って全配信するのを防ぐ）。
 *
 * M-76 の `suppressHotInProgress` は**意図的に数えない** — 対象を絞る条件ではなく
 * 「絞った結果からさらに除外する」修飾子であり、これを条件に数えると
 * 「商談中を除外」だけ ON の空セグメントが全員配信として通ってしまう。
 */
export function isEmptySegment(segment) {
    return (!segment.interestKey &&
        (!segment.washpassStatus || segment.washpassStatus.length === 0) &&
        !segment.promoTag &&
        typeof segment.inactiveSinceDays !== 'number' &&
        typeof segment.inspectionDueWithinDays !== 'number' &&
        typeof segment.minBookingCount !== 'number' &&
        segment.neverBooked !== true &&
        !segment.bookedCategoryId &&
        !segment.bookedMenuId &&
        !segment.hasEventType &&
        // M-165: 流入経路（区分 / 新規のみ）。
        !segment.firstSource &&
        segment.firstSourceNewOnly !== true &&
        // M-97: 選択肢が 1 つも無い `surveyAnswer` は絞り込めていない（= 条件に数えない）。
        !(segment.surveyAnswer && segment.surveyAnswer.optionIds.length > 0) &&
        // M-166: couponIds が空の `couponHolding` も同様に条件に数えない。
        !hasCouponHoldingCondition(segment));
}
/**
 * 1 顧客がセグメント条件に合致するか。指定された条件のみ AND で評価する。
 * - interestKey: その興味タグを持ち、minLevel（既定 estimated）以上の確度
 * - minStage: interestKey 指定時のみ。評価時減衰（`applyStageDecay` / M-72）を適用した
 *   **実効ステージ**が minStage 以上（保存値は書き換えない）。減衰ルールは M-74 で
 *   商材×テナント別に config 化 — `config` 引数（engines）から `resolveStageDecayRule`
 *   で解決する。`config` 省略時も商材別既定で評価する（テナント上書きが効かないだけの
 *   fail-soft / 後方互換）
 * - suppressHotInProgress: interestKey 指定時のみ（M-76 / §7 排他制御）。担当者が商談中
 *   （HOT 案件 `in_progress` かつ自動フォロー未解除）の顧客を除外する。未指定は除外なし
 * - churnRisk: interestKey 指定時のみ（M-75 / タイプ③離反検知）。評価時に
 *   `evaluateVisitChurn` で来店間隔の逸脱を判定し、churn_risk のみ一致。churn 無効・
 *   判定材料不足（insufficient_data）は不一致（fail-closed = 誤配信しない）
 * - washpassStatus: いずれかに一致（未設定は 'none' 扱い）
 * - promoTag: 手動タグを含む
 * - inactiveSinceDays: lastEventAt が now-N日 以前（未記録は「最も非アクティブ」扱いで合致）
 * - inspectionDueWithinDays: いずれかの車検満了日が [今日, 今日+N] (JST) に入る
 * - minBookingCount: 累計予約回数（bookingCount）が N 回以上（未設定は 0 扱い）
 * - neverBooked: 予約履歴が無い（bookingCount が 0 / 未設定）
 * - bookedCategoryId: そのカテゴリのメニューを予約したことがある
 * - bookedMenuId: そのメニューを予約したことがある
 * - hasEventType: その種別の行動ログを 1 回以上経験している
 * - surveyAnswer: アンケートの選択式設問で `optionIds` のいずれかを選んでいる（M-97 / OR）。
 *   判定は users ミラー（選択肢 id のみ・最新 20 アンケート）で、確認できないものは不一致
 *
 * - couponHolding: `ctx.couponHolders`（呼出元が coupon_usages から解決した lineUserId 集合）に
 *   含まれる（M-166）。**集合が渡されていなければ不一致**（fail-closed / 旧経路での黙殺防止）
 *
 * M-87: `lineBlocked === true`（LINE ブロック / 友だち解除済み）は**条件に関わらず常に不一致**。
 */
export function matchesSegment(user, segment, now = new Date(), config, ctx) {
    // M-87: ブロック / 友だち解除済みは push が構造的に届かないため、条件判定より前に落とす。
    // ここに置くのは `filterUsersBySegment` が即時配信・予約配信・人数プレビュー・KPI 母数の
    // **単一経路**だから（`isInterestOptedOut` と同じ理由）。セグメント条件ではなく無条件の
    // 除外なので `isEmptySegment` には数えない（「全配信」= 空セグメントでも除外は効く）。
    // 注: broadcast（LINE 全員配信）は宛先個別化できず抑制不能（LINE 仕様の構造的制約）。
    if (user.lineBlocked === true)
        return false;
    if (segment.interestKey) {
        // M-73 仕上げ: 「関心なし」を自己申告した商材は配信を抑制する（§6 A×self_declared /
        // 嫌われ防止）。最新回答という事実（lastSurveyAnswers）から評価時に導出するため、
        // 「関心あり」への答え直しで自然に解除される。単一経路（filterUsersBySegment）のため
        // 即時・予約配信・人数プレビュー・KPI 母数のすべてに効く。
        // 注: broadcast（LINE 全員配信）は宛先個別化できず抑制不能（LINE 仕様の構造的制約）。
        // M-76: 述語は `isInterestOptedOut` に一本化した（HOT 起票側と同じ事実で判断する）。
        if (isInterestOptedOut(user.lastSurveyAnswers, segment.interestKey)) {
            return false;
        }
        // M-76 §7 排他制御: 担当者が商談中（HOT 案件 in_progress）の顧客×商材は自動配信を
        // 止める（商談への割込み防止）。ここに置くのは `filterUsersBySegment` が即時配信・
        // 予約配信・人数プレビューの**単一経路**だから — 送信側（runSegmentPush）に足すと
        // プレビュー人数と実配信数が乖離し、管理画面で理由を説明できなくなる。
        // 3 日無対応の自動フォローが排他を解除した案件（suppressionReleased）は対象外
        // （機会損失防止 / `isHotDeliverySuppressed`）。
        if (segment.suppressHotInProgress === true &&
            isHotDeliverySuppressed(user.hotLeadStatuses?.[segment.interestKey])) {
            return false;
        }
        const tag = user.interestTags?.[segment.interestKey];
        if (!tag)
            return false;
        if (interestStrength(tag) < levelBaselineStrength(segment.minLevel ?? 'estimated')) {
            return false;
        }
        // M-72: 最低ステージ（attention は全員一致のため評価不要 = 指定なしと等価）。
        if (segment.minStage && stageRank(segment.minStage) > stageRank('attention')) {
            const stored = user.interestStages?.[segment.interestKey] ?? 'attention';
            // M-74: 商材別減衰（テナント上書き → 商材別既定の 2 層解決）。合成は M-119 で
            // `resolveEffectiveStage` に一本化（HOT 検知・顧客画面・KPI リプレイと同じ定義）。
            const effective = resolveEffectiveStage({ stage: stored, ...user.interestStageMeta?.[segment.interestKey] }, now.toISOString(), segment.interestKey, config);
            if (stageRank(effective.stage) < stageRank(segment.minStage))
                return false;
        }
        // M-75: 呼び戻し対象（タイプ③離反検知）。churn 設定はテナント上書き → 商材別既定の
        // 2 層解決。churn 無効・判定材料不足は不一致（fail-closed）。
        if (segment.churnRisk === true) {
            const verdict = evaluateVisitChurn(user.visitCycleMeta?.[segment.interestKey], now.toISOString(), resolveInterestEngine(segment.interestKey, config).churn);
            if (verdict.status !== 'churn_risk')
                return false;
        }
    }
    if (segment.washpassStatus && segment.washpassStatus.length > 0) {
        const status = user.washpassStatus ?? 'none';
        if (!segment.washpassStatus.includes(status))
            return false;
    }
    if (segment.promoTag) {
        if (!user.promoTags?.includes(segment.promoTag))
            return false;
    }
    if (typeof segment.inactiveSinceDays === 'number') {
        const cutoff = now.getTime() - segment.inactiveSinceDays * DAY_MS;
        const last = user.lastEventAt ? new Date(user.lastEventAt).getTime() : -Infinity;
        if (!(last <= cutoff))
            return false;
    }
    if (typeof segment.inspectionDueWithinDays === 'number') {
        const today = todayJst(now);
        const limit = addDaysJst(today, segment.inspectionDueWithinDays);
        const vehicles = user.vehicles ?? [];
        const hit = vehicles.some((v) => typeof v.inspectionDueDate === 'string' &&
            v.inspectionDueDate >= today &&
            v.inspectionDueDate <= limit);
        if (!hit)
            return false;
    }
    const bookingCount = user.bookingCount ?? 0;
    if (typeof segment.minBookingCount === 'number') {
        if (bookingCount < segment.minBookingCount)
            return false;
    }
    if (segment.neverBooked === true) {
        if (bookingCount > 0)
            return false;
    }
    if (segment.bookedCategoryId) {
        if (!user.bookedCategoryIds?.includes(segment.bookedCategoryId))
            return false;
    }
    if (segment.bookedMenuId) {
        if (!user.bookedMenuIds?.includes(segment.bookedMenuId))
            return false;
    }
    if (segment.hasEventType) {
        if (!((user.eventTypeCounts?.[segment.hasEventType] ?? 0) > 0))
            return false;
    }
    // M-165: 流入経路（最初の入口）。区分は users.firstSource から写す（未設定 = none）。
    if (segment.firstSource) {
        if (userFirstSourceBucket(user) !== segment.firstSource)
            return false;
    }
    if (segment.firstSourceNewOnly === true) {
        if (user.firstSourceIsNew !== true)
            return false;
    }
    // M-97: アンケートの回答内容で絞り込む。判定材料は `promoSurveyAnswer` が書く
    // users ミラー（選択肢 id のみ / 最新 SURVEY_ANSWER_MIRROR_LIMIT 件のアンケート）。
    //
    // **fail-closed**（判定できないときは不一致）にしている: 未回答・古すぎてミラーから
    // 溢れた・設問を作り替えて id が変わった、のいずれも「条件を満たすと確認できない」ので
    // 配信しない。ここで「一致」に倒すと、意図しない相手へ販促が飛ぶ。
    if (segment.surveyAnswer) {
        const { surveyId, questionId, optionIds } = segment.surveyAnswer;
        if (optionIds.length === 0)
            return false;
        const answered = user.surveyAnswers?.[surveyId]?.answers?.[questionId] ?? [];
        // optionIds は OR（いずれか 1 つでも選んでいれば一致）。
        if (!optionIds.some((id) => answered.includes(id)))
            return false;
    }
    // M-166: クーポン保有条件。判定材料（coupon_usages）は users に無いので、呼出元が解決した
    // 集合を見る。集合が無い = この経路は条件を評価できない → 誰にも一致させない（fail-closed）。
    // 人数プレビューも同じ関数を通るので、読込中は 0 人に見える（過大に見えるより安全）。
    if (hasCouponHoldingCondition(segment)) {
        const holders = ctx?.couponHolders;
        if (!holders)
            return false;
        if (!user.lineUserId || !holders.has(user.lineUserId))
            return false;
    }
    return true;
}
/** セグメントに合致する顧客のみを返す（非破壊）。 */
export function filterUsersBySegment(users, segment, now = new Date(), config, ctx) {
    return users.filter((u) => matchesSegment(u, segment, now, config, ctx));
}
/** セグメントに合致する人数（人数プレビュー用）。 */
export function countUsersBySegment(users, segment, now = new Date(), config, ctx) {
    let n = 0;
    for (const u of users)
        if (matchesSegment(u, segment, now, config, ctx))
            n += 1;
    return n;
}
/**
 * M-98: セグメント条件が特定のアンケートを参照しているか（純関数）。
 *
 * アンケート削除（`promoSurveyDelete`）の参照チェックが、プラン KPI・シナリオ分岐・
 * 予約配信の**3 経路で同じ判定**を使うための単一の真実源。今は `surveyAnswer` 条件しか
 * アンケートを参照しないが、セグメント条件に survey 参照が増えたときの追随点をここ 1 箇所に
 * 閉じ込める（増やした条件をここに足し忘れると、**使用中のアンケートが消せてしまい**
 * 配信済みリンクが「アンケートが見つかりません」になる）。
 *
 * `optionIds` が空でも参照とみなす — `matchesSegment` は不一致に倒すが、条件としては
 * 保存されており、アンケートを消すと編集画面で復元できなくなる。
 *
 * 呼出元は Firestore doc から読んだ値をそのまま渡す（型は付いていても中身は保証されない）
 * ため、`surveyAnswer` が object でないケースも黙って false に倒す。
 */
export function segmentReferencesSurvey(segment, surveyId) {
    const ref = segment?.surveyAnswer;
    return !!ref && typeof ref === 'object' && ref.surveyId === surveyId;
}
//# sourceMappingURL=promoSegment.js.map